import React, { useCallback, useMemo } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import { ConflictResolution, ExistingBlackoutDate, ManualRoom } from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';

import AddDateSection from './AddDateSection';
import StopsellManualDateRow from './StopsellManualDateRow';

interface Props {
  room: ManualRoom;
  roomIndex: number;
  blackoutDates: Array<ExistingBlackoutDate>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  rateResolutions: Record<string, ConflictResolution>;
  onAddDate: (roomIndex: number, start: string, end: string) => void;
  onEditDate: (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => void;
  onRemoveRoom: (roomIndex: number) => void;
  onRemoveDate: (roomIndex: number, dateIndex: number) => void;
  onToggleDate: (roomIndex: number, dateIndex: number, selected: boolean) => void;
  onResolve: (roomIndex: number, dateId: string, rateId: string, status: ConflictResolution) => void;
  onUndo: (roomIndex: number, dateId: string, rateId: string) => void;
}

export default function StopsellManualRoomCard({
  room,
  roomIndex,
  blackoutDates,
  catalogRoomRates,
  rateResolutions,
  onAddDate,
  onEditDate,
  onRemoveRoom,
  onRemoveDate,
  onToggleDate,
  onResolve,
  onUndo,
}: Props) {
  const handleRemoveRoom = useCallback(() => onRemoveRoom(roomIndex), [onRemoveRoom, roomIndex]);

  const handleRemoveDate = useCallback(
    (dateIndex: number) => onRemoveDate(roomIndex, dateIndex),
    [onRemoveDate, roomIndex],
  );

  const handleToggleDate = useCallback(
    (dateIndex: number, selected: boolean) => onToggleDate(roomIndex, dateIndex, selected),
    [onToggleDate, roomIndex],
  );

  const handleAddDate = useCallback(
    (start: string, end: string) => onAddDate(roomIndex, start, end),
    [onAddDate, roomIndex],
  );

  const handleEditDate = useCallback(
    (dateId: string, newRange: { start: string; end: string }) => onEditDate(roomIndex, dateId, newRange),
    [onEditDate, roomIndex],
  );

  const today = useMemo(() => toDayJs(new Date().toISOString().slice(0, 10)), []);
  const futureBlackouts = blackoutDates.filter((d) => !toDayJs(d.end).isBefore(today, 'day'));
  const existingLabel =
    futureBlackouts.length > 0 ? futureBlackouts.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack gap={0.5}>
          <Typography fontWeight={500}>{room.catalogRoomName}</Typography>
          {existingLabel && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <EventBusyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Existing: {existingLabel}
              </Typography>
            </Stack>
          )}
        </Stack>
        <IconButton size="small" onClick={handleRemoveRoom}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack gap={0.5} mb={1}>
        <AddDateSection onAdd={handleAddDate} />
      </Stack>

      {room.dates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dates added yet
        </Typography>
      ) : (
        <Stack gap={0.5}>
          {room.dates.map((date, dateIndex) => (
            <StopsellManualDateRow
              key={date.id}
              date={date}
              dateIndex={dateIndex}
              roomIndex={roomIndex}
              catalogRoomRates={catalogRoomRates}
              blackoutDates={blackoutDates}
              rateResolutions={rateResolutions}
              onToggle={handleToggleDate}
              onEdit={handleEditDate}
              onDelete={handleRemoveDate}
              onResolve={onResolve}
              onUndo={onUndo}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
